import { AlertTriangle, Clock, FileCheck, Video } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import type { BoardCard } from '@/types/board.types';

type OverdueStatus = 'overdue' | 'due-soon' | 'on-track' | null;

interface ApplicantCardProps {
  card: BoardCard;
  onClick: (id: string) => void;
}

export function ApplicantCard({ card, onClick }: ApplicantCardProps) {
  const { t } = useTranslation();

  const submittedDate = new Date(card.submittedAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  const dueAt = card.assignment?.dueAt;

  const overdueStatus: OverdueStatus = useMemo(() => {
    if (!dueAt) return null;
    const hoursRemaining = (new Date(dueAt).getTime() - Date.now()) / 3_600_000;
    if (hoursRemaining < 0) return 'overdue';
    if (hoursRemaining < 24) return 'due-soon';
    return 'on-track';
  }, [dueAt]);

  const formattedDueDate = useMemo(() => {
    if (!dueAt) return '';
    return new Date(dueAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }, [dueAt]);

  return (
    <button
      type='button'
      onClick={() => onClick(card.id)}
      className={cn(
        'w-full text-left bg-white rounded-lg border border-gray-200 p-3 shadow-sm hover:shadow-md hover:border-gray-300 transition-all cursor-pointer',
        overdueStatus === 'overdue' && 'border-l-4 border-l-red-500',
        overdueStatus === 'due-soon' && 'border-l-4 border-l-amber-400',
      )}>
      {/* Name + date */}
      <div className='flex items-start justify-between gap-2 mb-1.5'>
        <p className='text-sm font-semibold text-gray-900 truncate'>
          {card.firstName} {card.lastName}
        </p>
        <span className='text-xs text-gray-400 whitespace-nowrap'>{submittedDate}</span>
      </div>

      {/* Application number + job posting */}
      <p className='text-xs text-gray-500 mb-2 truncate'>
        {card.applicationNumber}
        {card.jobPostingTitle ? ` · ${card.jobPostingTitle}` : ''}
      </p>

      {/* Roles */}
      {card.roles.length > 0 && (
        <div className='flex flex-wrap gap-1 mb-2'>
          {card.roles.map((role) => (
            <span key={role} className='inline-block rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600'>
              {role}
            </span>
          ))}
        </div>
      )}

      {/* Due date */}
      {dueAt && (
        <div
          className={cn(
            'flex items-center gap-1 mb-2 text-xs',
            overdueStatus === 'overdue' && 'text-red-600 font-medium',
            overdueStatus === 'due-soon' && 'text-amber-600',
            overdueStatus === 'on-track' && 'text-gray-500',
          )}>
          <Clock size={12} />
          <span>
            {overdueStatus === 'overdue' ? t('board.card.overdue') : t('board.card.dueBy', { date: formattedDueDate })}
          </span>
        </div>
      )}

      {/* Badges row */}
      <div className='flex items-center gap-2 text-xs'>
        <span
          className={cn(
            'inline-flex items-center gap-1',
            card.docCompletionPercent === 100 ? 'text-green-600' : 'text-gray-500',
          )}>
          <FileCheck size={12} />
          {t('board.card.docsComplete', { percent: card.docCompletionPercent })}
        </span>

        {card.hasVideo && (
          <span className='inline-flex items-center gap-1 text-purple-600'>
            <Video size={12} />
            {t('board.card.videoReady')}
          </span>
        )}

        {card.flagCount > 0 && (
          <span className='inline-flex items-center gap-1 text-amber-600'>
            <AlertTriangle size={12} />
            {t('board.card.flags', { count: card.flagCount })}
          </span>
        )}
      </div>
    </button>
  );
}
